import { Component, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback component */
  fallback?: ReactNode;
  /**
   * Remontée de l'erreur. Absente : Sentry, chargé à la demande (comportement de tous
   * les sites). remup.fr passe la sienne depuis KST-2708 : il n'embarque plus Sentry
   * côté navigateur et remonte ses erreurs par PostHog.
   */
  signaler?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Shared ErrorBoundary component for all Konstellation sites.
 * Catches unhandled errors in the React component tree and displays
 * a user-friendly error page with reload and home navigation options.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Erreur capturée:', error, errorInfo);
    if (this.props.signaler) {
      try {
        this.props.signaler(error, errorInfo);
      } catch {
        /* la remontée ne doit jamais casser l'écran d'erreur */
      }
      return;
    }
    // Remontée à Sentry.
    //
    // ⚠️ Ne JAMAIS revenir à `require('@sentry/react')` ici : les apps sont bundlées
    // en ESM (Vite / Next), `require` n'y existe pas, l'appel lève aussitôt et le
    // catch l'avalait en silence. Résultat : pendant des mois, un visiteur voyait
    // l'écran d'erreur et personne n'était prévenu (constaté le 28/07/2026).
    //
    // Une erreur capturée par une ErrorBoundary React ne remonte pas non plus par
    // window.onerror : sans cet appel explicite, elle est définitivement perdue.
    //
    // L'import dynamique fonctionne en ESM et se résout depuis l'app qui bundle ce
    // composant (les 5 apps Vite ont @sentry/react). Si le SDK est absent, le .catch
    // laisse simplement passer.
    import('@sentry/react')
      .then((Sentry) => {
        Sentry?.captureException?.(error, {
          contexts: { react: { componentStack: errorInfo.componentStack } },
        });
      })
      .catch(() => {
        /* SDK absent de cette app : pas de remontée, l'écran d'erreur reste affiché */
      });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-gray-900">
                Une erreur inattendue s'est produite
              </h1>
              <p className="text-gray-500">
                Veuillez recharger la page ou retourner à l'accueil.
                Si le problème persiste, contactez-nous.
              </p>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-gray-100 rounded-lg p-4 text-left">
                <p className="text-xs font-mono text-gray-600 break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Recharger la page
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
